import { Clock, Activity } from 'lucide-react';

/* =========================
   Market status utilities
========================= */

// `t` is the i18next translation function (from a caller's useTranslation()
// hook) -- this is a plain util, not a component, so it can't call the hook
// itself. Falls back to the English label if no `t` is passed in.
export const getMarketStatus = (marketData, t) => {
  const tr = (key, fallback) => (t ? t(key) : fallback);

  if (!marketData) return {
    label: tr('stocks.marketStatus.closed', 'Market Closed'),
    color: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-100 border-gray-300 dark:bg-gray-800 dark:border-gray-600',
    icon: Clock
  };

  const market = marketData.market?.toLowerCase();
  const earlyHours = marketData.earlyHours;
  const afterHours = marketData.afterHours;

  if (market === 'open') {
    return {
      label: tr('stocks.marketStatus.open', 'Market Open'),
      color: 'text-green-700 dark:text-green-400',
      bgColor: 'bg-green-50 border-green-300 dark:bg-green-900/30 dark:border-green-700',
      icon: Activity
    };
  }

  if (market?.includes('extended')) {
    if (earlyHours) {
      return {
        label: tr('stocks.marketStatus.preMarket', 'Pre-Market'),
        color: 'text-yellow-700 dark:text-yellow-400',
        bgColor: 'bg-yellow-50 border-orange-600 dark:bg-yellow-900/30 dark:border-yellow-700',
        icon: Clock
      };
    }
    if (afterHours) {
      return {
        label: tr('stocks.marketStatus.afterHours', 'After Hours'),
        color: 'text-blue-700 dark:text-blue-400',
        bgColor: 'bg-blue-50 border-blue-300 dark:bg-blue-900/30 dark:border-blue-700',
        icon: Clock
      };
    }
    return {
      label: tr('stocks.marketStatus.extendedHours', 'Extended Hours'),
      color: 'text-orange-700 dark:text-orange-400',
      bgColor: 'bg-orange-50 border-orange-300 dark:bg-orange-900/30 dark:border-orange-700',
      icon: Clock
    };
  }

  return {
    label: tr('stocks.marketStatus.closed', 'Market Closed'),
    color: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-100 border-gray-300 dark:bg-gray-800 dark:border-gray-600',
    icon: Clock
  };
};

/* =========================
   Daily bars utilities
========================= */

export const parsePeriodDate = (item) => {
  if (item?.period && /^\d{8}$/.test(String(item.period))) {
    const p = String(item.period);
    return new Date(`${p.slice(0, 4)}-${p.slice(4, 6)}-${p.slice(6, 8)}`);
  }
  if (item?.date) {
    const d = new Date(item.date);
    if (!Number.isNaN(d.getTime())) return d;
  }
  if (item?.t || item?.timestamp) {
    const d = new Date(item.t ?? item.timestamp);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
};

export const getOpen  = (item) => item?.period_open  ?? item?.open  ?? item?.o ?? null;
export const getHigh  = (item) => item?.period_high  ?? item?.high  ?? item?.h ?? null;
export const getLow   = (item) => item?.period_low   ?? item?.low   ?? item?.l ?? null;
export const getClose = (item) => item?.period_close ?? item?.close ?? item?.c ?? null;
export const getVol   = (item) => item?.total_volume ?? item?.volume ?? item?.v ?? 0;

const isSameUTCDate = (a, b) =>
  a &&
  b &&
  a.getUTCFullYear() === b.getUTCFullYear() &&
  a.getUTCMonth() === b.getUTCMonth() &&
  a.getUTCDate() === b.getUTCDate();

// US Market holidays (basic list)
const US_HOLIDAYS = [
  '2024-01-01', '2024-01-15', '2024-02-19', '2024-03-29', '2024-05-27', 
  '2024-06-19', '2024-07-04', '2024-09-02', '2024-11-28', '2024-12-25',
  '2025-01-01', '2025-01-20', '2025-02-17', '2025-04-18', '2025-05-26',
  '2025-06-19', '2025-07-04', '2025-09-01', '2025-11-27', '2025-12-25',
];

const isWeekend = (date) => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

const isHoliday = (date) => {
  const dateStr = date.toISOString().split('T')[0];
  return US_HOLIDAYS.includes(dateStr);
};

const isMarketDay = (date) => {
  return !isWeekend(date) && !isHoliday(date);
};

export const findPreviousClose = (bars = []) => {
  if (!Array.isArray(bars) || bars.length === 0) return null;

  const sorted = [...bars]
    .filter(bar => parsePeriodDate(bar))
    .sort((a, b) => (parsePeriodDate(b)?.getTime() ?? 0) - (parsePeriodDate(a)?.getTime() ?? 0));

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let i = 0; i < sorted.length; i++) {
    const barDate = parsePeriodDate(sorted[i]);
    if (!barDate) continue;
    
    barDate.setHours(0, 0, 0, 0);
    
    if (barDate >= today) continue;
    
    if (isMarketDay(barDate)) {
      return getClose(sorted[i]);
    }
  }

  return sorted.length > 0 ? getClose(sorted[0]) : null;
};

export const get52WeekRange = (bars) => {
  if (!Array.isArray(bars) || bars.length === 0) return { high: null, low: null };
  const sorted = [...bars].sort((a, b) => {
    const da = parsePeriodDate(a)?.getTime() ?? 0;
    const db = parsePeriodDate(b)?.getTime() ?? 0;
    return da - db;
  });
  const latest = parsePeriodDate(sorted.at(-1));
  if (!latest) return { high: null, low: null };
  const earliest = new Date(latest.getTime() - 52 * 7 * 24 * 60 * 60 * 1000);
  const inWin = sorted.filter((x) => {
    const d = parsePeriodDate(x);
    return d && d >= earliest && d <= latest;
  });
  if (inWin.length === 0) return { high: null, low: null };
  const highs = inWin.map(getHigh).filter((v) => v != null);
  const lows = inWin.map(getLow).filter((v) => v != null);
  if (highs.length === 0 || lows.length === 0) return { high: null, low: null };
  return { high: Math.max(...highs), low: Math.min(...lows) };
};

export const averageVolume = (bars, days = 30) => {
  if (!Array.isArray(bars) || bars.length === 0) return null;
  const sorted = [...bars].sort((a, b) => {
    const da = parsePeriodDate(b)?.getTime() ?? 0;
    const db = parsePeriodDate(a)?.getTime() ?? 0;
    return da - db;
  });
  const today = new Date();
  const first = sorted[0];
  const dropToday =
    first && isSameUTCDate(parsePeriodDate(first), today) ? sorted.slice(1) : sorted;
  const slice = dropToday.slice(0, Math.min(days, dropToday.length));
  if (slice.length === 0) return null;
  const total = slice.reduce((s, x) => s + (getVol(x) || 0), 0);
  return total / slice.length;
};